import React, { useCallback, useEffect, useState } from 'react'
import { Button, BUTTON_SIZE, BUTTON_THEME, Select } from '@st-operations/uikit/src/v2'

import {
  cancelPush,
  copyPush,
  getPushCalls,
  resumePush,
  retryFailed,
} from '_services/api-integrations'

import {
  BATCH_STATES,
  STATUS_CLASSES,
  STATUS_LABELS,
  actionOf,
  isStale,
  sinceHeartbeat,
} from '../constants'
import { downloadResults } from '../download-results'

// `older`: outcomes from before a segment already in the partner's system was
// updated rather than left alone - offered only on a push that has them.
// `batches`: only on a push sent in batches (Yahoo).
const STATE_FILTERS = [
  { value: '', label: 'Everything' },
  { value: 'created', label: 'Created' },
  { value: 'updated', label: 'Updated' },
  { value: 'already_there', label: 'Already there', older: true },
  { value: 'rate_set', label: 'Rate set', older: true },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'sent', label: 'Waiting on the partner', batches: true },
  { value: 'pending', label: 'Not reached yet' },
]

// The steps the partner took when it says (TTD: "created, rate_set" for a new
// segment), otherwise the outcome as it is stored.
const resultOf = (call) => {
  if (call.steps?.length) return call.steps.join(', ')
  // in a request the partner has, and is still working on
  if (call.state === 'sent') return 'sent, waiting'
  return call.state
}

// what went wrong with a request to the partner, if anything
const batchErrors = (batch) => batch.error || (batch.errors || []).join('; ')

const PAGE = 50

// "Oct 1, 10:51 PM"
const when = (date) =>
  new Date(date).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

// What a push did, and whether it is still alive.
//
// The point of the heartbeat line is that ops should not have to judge
// whether a slow push is a dead one. Resume only appears once the server
// would also consider it stalled - and by then the sweeper has usually picked
// it up on its own.
// `onOpenPush` takes the new draft's id. "Run again" and "Retry" both make a
// new draft rather than touching this record, so the useful thing afterwards
// is to be looking at that draft - not this page again.
const PushStatus = ({ push, partner, partnerName, onChanged, onOpenPush }) => {
  const [calls, setCalls] = useState([])
  const [count, setCount] = useState(0)
  const [state, setState] = useState('')
  const [skip, setSkip] = useState(0)
  const [busy, setBusy] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState(null)
  // rows showing what was sent and what came back
  const [open, setOpen] = useState({})

  const toggle = (id) => setOpen({ ...open, [id]: !open[id] })

  const loadCalls = useCallback(async () => {
    try {
      const params = { limit: PAGE, skip, sort: 'created_at,1' }
      if (state) params.state = state
      const data = await getPushCalls(push._id, params)
      setCalls(data?.items || [])
      setCount(data?.count || 0)
    } catch (e) {
      setError(e?.message || 'could not load the results')
    }
  }, [push._id, state, skip])

  useEffect(() => {
    loadCalls()
  }, [loadCalls])

  // while it is running the rows keep changing underneath us
  useEffect(() => {
    if (push.status !== 'running') return undefined
    const timer = setInterval(loadCalls, 5000)
    return () => clearInterval(timer)
  }, [push.status, loadCalls])

  const act = async (fn, after) => {
    setBusy(true)
    setError(null)
    try {
      const result = await fn(push._id)
      if (after) after(result)
      if (onChanged) onChanged()
    } catch (e) {
      setError(e?.message || 'that did not work')
    }
    setBusy(false)
  }

  // every row the Show filter lets through, not just this page of them
  const download = async () => {
    setDownloading(true)
    setError(null)
    try {
      await downloadResults(push, state)
    } catch (e) {
      setError(e?.message || 'could not download the results')
    }
    setDownloading(false)
  }

  const counts = push.counts || {}
  const stale = isStale(push)
  const running = push.status === 'running'

  // the outcome in numbers - outcomes only older pushes have appear only on them
  const tiles = [
    { key: 'created', label: 'created' },
    { key: 'updated', label: 'updated' },
    { key: 'already_there', label: 'already there', older: true },
    { key: 'rate_set', label: 'rate set', older: true },
    { key: 'failed', label: 'failed', bad: true },
    { key: 'skipped', label: 'skipped' },
    { key: 'total', label: 'total' },
  ].filter((tile) => !tile.older || counts[tile.key])

  const progress = counts.total ? Math.min(100, ((push.position || 0) / counts.total) * 100) : 0

  const meta = [
    partnerName || push.partner,
    push.mapping_id && `mapping ID ${push.mapping_id}`,
    push.started_at && `started ${when(push.started_at)}`,
    push.finished_at && `finished ${when(push.finished_at)}`,
  ].filter(Boolean)

  const shown = calls.length ? `${skip + 1}–${skip + calls.length} of ${count}` : `0 of ${count}`

  const batches = push.batches || []
  const who = partnerName || push.partner

  return (
    <div className="push-status">
      <If condition={push.mocked}>
        <div className="mock-banner">
          <strong>This push was mocked.</strong> No partner was contacted — the results below say
          what would have happened.
        </div>
      </If>

      {/* what this push is, and what can be done with it */}
      <div className="panel">
        <div className="panel__head">
          <div>
            <div className="flex items-center gap-3">
              <span className={`status-pill ${STATUS_CLASSES[push.status] || ''}`}>
                {STATUS_LABELS[push.status] || push.status}
              </span>
              <span className="panel__title">{actionOf(push, partner).label}</span>
            </div>
            <div className="meta">{meta.join(' · ')}</div>
          </div>

          <div className="panel__actions">
            <If condition={running}>
              <Button onClick={() => act(cancelPush)} disabled={busy}>
                Cancel
              </Button>
            </If>
            <If condition={stale || ['failed', 'cancelled'].includes(push.status)}>
              <Button
                theme={BUTTON_THEME.SECONDARY}
                onClick={() => act(resumePush)}
                disabled={busy}
              >
                Resume
              </Button>
            </If>
            <If condition={counts.failed > 0}>
              <Button
                theme={BUTTON_THEME.SECONDARY}
                onClick={() => act(retryFailed, (draft) => onOpenPush?.(draft._id))}
                disabled={busy}
              >
                Retry the {counts.failed} that failed
              </Button>
            </If>
            <If condition={!running}>
              <Button
                theme={BUTTON_THEME.SECONDARY}
                onClick={() => act(copyPush, (draft) => onOpenPush?.(draft._id))}
                disabled={busy}
              >
                Run again
              </Button>
            </If>
          </div>
        </div>

        <If condition={running}>
          <div className="progress">
            <div className="progress__bar">
              <div style={{ width: `${progress}%` }} />
            </div>
            <div className="meta">
              {push.position || 0} of {counts.total || '?'}
              {' · '}
              <span className={stale ? 'text-red-600 font-medium' : ''}>
                last activity {sinceHeartbeat(push) || 'unknown'}
              </span>
            </div>
          </div>
        </If>

        <If condition={running && push.waiting_for}>
          <div className="callout callout--info">
            {push.waiting_for.taxonomy
              ? `Waiting for the ${who} push for ${push.waiting_for.taxonomy} to finish.`
              : `Waiting for another ${who} push to finish.`}{' '}
            {who} pushes take turns: taxonomies can share a folder, and two changing it at once
            would undo each other.
          </div>
        </If>

        <If condition={stale}>
          <div className="callout callout--warn">
            This push has gone quiet. The server checks for stalled pushes every minute and usually
            restarts them on its own — give it a moment before resuming by hand.
          </div>
        </If>

        <If condition={push.error}>
          <div className="callout callout--bad">{push.error}</div>
        </If>

        <If condition={error}>
          <div className="callout callout--bad">{error}</div>
        </If>
      </div>

      {/* the outcome, in numbers */}
      <div className="stat-grid">
        <For each="tile" of={tiles}>
          <div
            key={tile.key}
            className={`stat ${tile.bad && counts[tile.key] ? 'stat--bad' : ''} ${
              tile.key === 'total' ? 'stat--total' : ''
            }`}
          >
            <div className="value">{counts[tile.key] || 0}</div>
            <div className="label">{tile.label}</div>
          </div>
        </For>
      </div>

      {/* the requests sent to a partner that takes changes in batches (Yahoo) */}
      <If condition={batches.length}>
        <div className="panel panel--flush">
          <div className="panel__head panel__head--padded">
            <div>
              <div className="panel__title">Requests to {who}</div>
              <div className="meta">
                {who} works on each request after taking it. They are checked every few minutes
                until {who} accepts or refuses them.
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="results-table">
              <thead>
                <tr>
                  <th>Request</th>
                  <th>Segments</th>
                  <th>State</th>
                  <th>Sent</th>
                  <th>Last checked</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                <For each="batch" of={batches}>
                  <tr key={batch.key}>
                    <td>
                      {batch.label}
                      <If condition={batch.request?.resource_id}>
                        <div className="mono text-xs text-gray-500">
                          {batch.request.resource_id}
                        </div>
                      </If>
                    </td>
                    <td>{batch.size ?? '-'}</td>
                    <td>
                      <span className={`status-pill ${BATCH_STATES[batch.state]?.className || ''}`}>
                        {BATCH_STATES[batch.state]?.label || batch.raw_state || batch.state}
                      </span>
                    </td>
                    <td>{batch.sent_at ? when(batch.sent_at) : '-'}</td>
                    <td>
                      {batch.checked_at ? when(batch.checked_at) : '-'}
                      <If condition={batch.check_error}>
                        <div className="text-xs text-amber-700">
                          last check failed: {batch.check_error}
                        </div>
                      </If>
                    </td>
                    <td className={batchErrors(batch) ? 'text-red-600' : 'text-gray-500'}>
                      {batchErrors(batch) || '-'}
                    </td>
                  </tr>
                </For>
              </tbody>
            </table>
          </div>
        </div>
      </If>

      {/* audience by audience */}
      <div className="panel panel--flush">
        <div className="panel__head panel__head--padded">
          <div>
            <div className="panel__title">Results</div>
            <div className="meta">{shown}</div>
          </div>
          <div className="results-filter">
            <span className="meta">Show</span>
            <div style={{ width: 220 }}>
              <Select
                useReactSelect
                name="state"
                value={STATE_FILTERS.find((f) => f.value === state)}
                options={STATE_FILTERS.filter(
                  (f) => (!f.older || counts[f.value]) && (!f.batches || batches.length)
                )}
                onChange={(option, name, val) => {
                  setSkip(0)
                  setState(val || '')
                }}
              />
            </div>
            <Button
              theme={BUTTON_THEME.SECONDARY}
              size={BUTTON_SIZE.SMALL}
              onClick={download}
              disabled={downloading || !count}
            >
              Download CSV
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="results-table">
            <thead>
              <tr>
                <th>Audience</th>
                <th>Sent as</th>
                <th>ID</th>
                <th>Result</th>
                <th>Error</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <If condition={!calls.length}>
                <tr>
                  <td colSpan={6} className="empty">
                    Nothing here yet.
                  </td>
                </tr>
              </If>
              <For each="call" of={calls}>
                <React.Fragment key={call._id}>
                  <tr>
                    <td>{call.audience?.name}</td>
                    <td>
                      {call.synced_name || '-'}
                      <If condition={call.duplicate_name}>
                        <div className="text-xs text-amber-700">shares this name with another</div>
                      </If>
                    </td>
                    <td className="mono">{call.external_id || '-'}</td>
                    <td>
                      <span className={`result result--${call.state}`}>{resultOf(call)}</span>
                    </td>
                    {/* only what went wrong - the error, or why it was skipped */}
                    <td className={call.error ? 'text-red-600' : 'text-gray-500'}>
                      {call.error || (call.state === 'skipped' ? call.reason : '') || '-'}
                    </td>
                    <td className="text-right">
                      <If condition={call.request || call.response}>
                        <Button
                          theme={BUTTON_THEME.GHOST}
                          size={BUTTON_SIZE.SMALLER}
                          onClick={() => toggle(call._id)}
                        >
                          {open[call._id] ? 'Hide' : 'Details'}
                        </Button>
                      </If>
                    </td>
                  </tr>
                  <If condition={open[call._id]}>
                    <tr className="details-row">
                      <td colSpan={6}>
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <div className="details-label">What we sent</div>
                            <pre>{JSON.stringify(call.request ?? null, null, 2)}</pre>
                          </div>
                          <div>
                            <div className="details-label">What came back</div>
                            <pre>{JSON.stringify(call.response ?? null, null, 2)}</pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </If>
                </React.Fragment>
              </For>
            </tbody>
          </table>
        </div>

        <If condition={count > PAGE}>
          <div className="pager">
            <span className="meta">{shown}</span>
            <div className="flex gap-2">
              <Button
                theme={BUTTON_THEME.SECONDARY}
                size={BUTTON_SIZE.SMALL}
                onClick={() => setSkip(Math.max(0, skip - PAGE))}
                disabled={skip === 0}
              >
                Previous
              </Button>
              <Button
                theme={BUTTON_THEME.SECONDARY}
                size={BUTTON_SIZE.SMALL}
                onClick={() => setSkip(skip + PAGE)}
                disabled={skip + PAGE >= count}
              >
                Next
              </Button>
            </div>
          </div>
        </If>
      </div>
    </div>
  )
}

export default PushStatus
